import { Link, useLoaderData } from "@remix-run/react";

import { buildMeta, getCompanyFromMatches } from "~/utils/meta-helpers";
import { serverFetchSafe } from "~/utils/server-fetch.server";

import { useHashScroll } from "../components/CuadernoVivo/useHashScroll";
import { WashiTape } from "../components/CuadernoVivo/WashiTape";
import { FAQPreviewSection } from "../components/HomeSections/FAQPreviewSection";
import { courseShape, pickIntroCourse } from "../lib/courseShape";

import type { MetaFunction } from "@remix-run/node";
import type { GetAvailableCoursesResponse, GetCourseResponse } from "~/api/generated-api";

/**
 * Loader de SERVIDOR: trae la forma REAL del curso introductorio (capítulos, clases, preguntas
 * rápidas, evaluaciones) para la respuesta "¿Cuánto tiempo me lleva?" de la FAQ. Antes ese número
 * estaba escrito a mano en i18n y mentía ("4 capítulos y 7 clases" con el curso en 10).
 *
 * Es una landing pública: la FAQ tiene que salir entera en el HTML (y en el JSON-LD FAQPage),
 * así que el dato se resuelve acá y no con una query en el cliente. Si la API no contesta, la
 * respuesta cae a una versión sin números — nunca a un número inventado.
 */
export const loader = async () => {
  const list = await serverFetchSafe<GetAvailableCoursesResponse>(
    "/api/course/available-courses?page=1&perPage=100&language=es",
  );
  const intro = pickIntroCourse(list?.data ?? []);
  if (!intro?.slug) return { introShape: null };

  const detail = await serverFetchSafe<GetCourseResponse>(
    `/api/course?id=${encodeURIComponent(intro.slug)}&language=es`,
  );
  return { introShape: courseShape(detail?.data) };
};

export const meta: MetaFunction = ({ matches }) => {
  const company = getCompanyFromMatches(matches);
  return buildMeta({
    title: `Contacto | ${company}`,
    description: "Escribinos: te respondemos a la brevedad, en español llano.",
    path: "/contact",
  });
};

const CONTACT_EMAIL = "hola@cursosacademia.ar";

/**
 * Contacto — Cuaderno Vivo style: a paper card with the real channels. No promised
 * response times ("a la brevedad", decisión de Rodrigo) and no dead-end forms.
 */
export default function ContactPage() {
  const { introShape } = useLoaderData<typeof loader>();
  // El link del footer apunta a /contact#faq: sin esto la navegación SPA llega a la
  // página pero nadie scrollea hasta el ancla (la home hace exactamente lo mismo).
  useHashScroll();
  return (
    <section className="landing-grain relative bg-landing-cream/70 px-5 py-20 backdrop-blur-[1px] sm:px-8 sm:py-24">
      <div className="relative mx-auto max-w-[640px]">
        <div className="text-center">
          <span className="landing-eyebrow block font-bold text-landing-red">Contacto</span>
          <h1 className="font-display mt-3 text-[clamp(28px,4vw,40px)] font-extrabold leading-tight tracking-tight text-landing-text">
            Escribinos. Somos gente, no un bot.
          </h1>
          <p className="mx-auto mt-3 max-w-[48ch] text-[15px] leading-relaxed text-landing-muted">
            Dudas sobre los cursos, las pre-inscripciones, tu cuenta o lo que sea — te respondemos a
            la brevedad, en español llano.
          </p>
        </div>

        <div className="relative mt-10 rounded-[4px] bg-landing-paper p-8 shadow-landing-xl sm:p-10">
          <WashiTape variant={1} />
          <div className="flex flex-col gap-6">
            <div>
              <p className="landing-eyebrow font-bold text-landing-teal">Por email</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-cuelume-press
                data-cuelume-release
                className="font-display mt-1 inline-block text-[clamp(18px,2.6vw,24px)] font-bold text-landing-text underline decoration-landing-red/40 underline-offset-4 transition-colors hover:decoration-landing-red"
              >
                {CONTACT_EMAIL}
              </a>
              <p className="mt-1 text-[13.5px] text-landing-muted">
                Contanos tu caso con tus palabras — no hace falta ningún tecnicismo.
              </p>
            </div>
            <div className="border-t-[1.5px] border-dashed border-landing-border pt-5">
              <p className="landing-eyebrow font-bold text-landing-teal">¿Duda rápida?</p>
              <p className="mt-1 text-[14.5px] leading-relaxed text-landing-text">
                Capaz ya está respondida en{" "}
                <Link
                  to="#faq"
                  className="font-bold text-landing-red underline decoration-landing-red/30 underline-offset-4 hover:decoration-landing-red"
                >
                  las preguntas más comunes
                </Link>
                .
              </p>
            </div>
            <div className="border-t-[1.5px] border-dashed border-landing-border pt-5">
              <p className="landing-eyebrow font-bold text-landing-teal">¿Todavía no empezaste?</p>
              <p className="mt-1 text-[14.5px] leading-relaxed text-landing-text">
                <Link
                  to="/#cursos"
                  className="font-bold text-landing-red underline decoration-landing-red/30 underline-offset-4 hover:decoration-landing-red"
                >
                  Tu primer curso es gratis
                </Link>{" "}
                — sin tarjeta, con tu WhatsApp.
              </p>
            </div>
          </div>
        </div>

        <p className="landing-hand mt-6 rotate-[-1.5deg] text-center text-[18px] text-landing-teal">
          leemos todo, en serio ✏️
        </p>
      </div>

      {/* Las preguntas frecuentes viven acá y no en la home: quien tiene una duda entra a
          Contacto, y en la home ocupaban una pantalla que casi nadie desplegaba. Van DEBAJO
          de las vías de contacto —primero cómo escribirnos, después qué capaz ya está
          contestado— y el ancla `#faq` es a donde apunta el link del footer. */}
      <FAQPreviewSection introShape={introShape} />
    </section>
  );
}
